import React, { useMemo, useState, useRef, useEffect, useCallback } from 'react';
import {
  Loader2, FileVideo, Clock, Settings, FileText, Mic, Sparkles,
  Timer, Pause, AlertTriangle, CheckCircle2, BarChart3, RotateCcw, ChevronDown, History,
  Play, Volume2, Volume1, StopCircle, Zap, Crown, Clapperboard
} from 'lucide-react';
import type { TtsVoiceOption } from '../hooks/useTtsPreview';
import type { useSegmentAudio } from '../hooks/useSegmentAudio';
import type { RecorderHealthAPI } from '../hooks/useRecorderHealth';
import { usePreviewPlayer } from '../hooks/usePreviewPlayer';
import { VOICE_OPTIONS } from '../utils/voiceList';
import { TimelineMultiTrack, type TimelineEvent } from './TimelineMultiTrack';

interface TranscriptionSegment {
  index: number;
  start: string;
  end: string;
  text: string;
  originalText?: string;
  resolvedAddedSec?: number;
}

interface RefinementVersion {
  label: string;
  segments: Array<{ index: number; start: string; end: string; text: string; resolvedAddedSec?: number }>;
  timestamp: number;
  analysis?: string;
}

interface StudioTabProps {
  previewUrl: string;
  legendaUrl: string;
  timer: number;
  formatTime: (t: number) => string;
  videoScript: string;
  setVideoScript: (v: string) => void;
  transcriptionSegments: TranscriptionSegment[];
  isTranscribing: boolean;
  isRefining: boolean;
  videoBlob: Blob | null;
  handleTranscribe: () => void;
  handleRefine: () => void;
  handleUpdateSegment: (index: number, text: string) => void;
  hasTranscription: boolean;
  setShowManualInput: (v: boolean) => void;
  // Editing segment states
  editingSegmentIndex: number | null;
  setEditingSegmentIndex: (v: number | null) => void;
  editingSegmentText: string;
  setEditingSegmentText: (v: string) => void;
  // Version management
  refinementVersions: RefinementVersion[];
  activeVersionIndex: number; // -1 = original
  handleSelectVersion: (index: number) => void;
  handleRestoreOriginal: () => void;
  // Playback
  playSegment?: (videoUrl: string, startStr: string, endStr: string, index: number, type: 'original' | 'processed') => void;
  playSegmentBruto?: (startStr: string, endStr: string, index: number) => void;
  stopSegment?: () => void;
  playingSegment?: { index: number; type: 'original' | 'processed' } | null;
  // TTS Preview
  ttsVoices?: TtsVoiceOption[];
  ttsSelectedVoiceId?: string;
  ttsSetSelectedVoiceId?: (id: string) => void;
  ttsGetVoice?: () => SpeechSynthesisVoice | null;
  // Segment Audio (generation + versioning)
  segAudio?: ReturnType<typeof useSegmentAudio>;
  // Timeline events (clicks, routes recorded during capture)
  timelineEvents?: TimelineEvent[];
  // Health monitor API for resource tracking
  healthApi?: RecorderHealthAPI;
  // Render callback
  onRenderStart?: (audioMode: 'bruto' | 'premium', voiceId: string) => void;
  isRendering?: boolean;
  setVoiceName?: (name: string) => void;
  onFixOverflows?: () => void;
}

// ── Helper: parse VTT timestamp "HH:MM:SS.mmm" to seconds ──
function parseVttTime(vtt: string | undefined | null): number {
  if (!vtt || typeof vtt !== 'string') return 0;
  const parts = vtt.split(':');
  if (parts.length === 3) {
    const h = parseFloat(parts[0]) || 0;
    const m = parseFloat(parts[1]) || 0;
    const s = parseFloat(parts[2]) || 0;
    return h * 3600 + m * 60 + s;
  }
  if (parts.length === 2) {
    const m = parseFloat(parts[0]) || 0;
    const s = parseFloat(parts[1]) || 0;
    return m * 60 + s;
  }
  return parseFloat(vtt) || 0;
}

// ── Helper: format seconds as "Xs" or "X.Xs" ──
function formatDuration(sec: number): string {
  if (sec < 0.1) return '0s';
  if (sec < 10) return sec.toFixed(1) + 's';
  return Math.round(sec) + 's';
}

// ── Helper: estimate speech duration based on word count (~2.3 words/sec for pt-BR TTS) ──
function estimateSpeechDuration(text: string): number {
  const wordCount = text.trim().split(/\s+/).filter(Boolean).length;
  return wordCount / 2.3; // avg speaking rate for pt-BR TTS
}

// ── Segment timing health analysis ──
type TimingHealth = 'ok' | 'tight' | 'overflow' | 'silence';

function getTimingHealth(segDurationSec: number, speechEstimateSec: number, pauseAfterSec: number): TimingHealth {
  const ratio = speechEstimateSec / segDurationSec;
  if (ratio > 1.15) return 'overflow';   // TTS will likely overflow segment
  if (ratio > 0.95) return 'tight';      // Very tight fit
  if (pauseAfterSec > 3.0) return 'silence'; // Long silence gap
  return 'ok';
}

const healthColors: Record<TimingHealth, string> = {
  ok: '#22c55e',
  tight: '#f59e0b',
  overflow: '#ef4444',
  silence: '#8b5cf6',
};

const healthIcons: Record<TimingHealth, React.ReactNode> = {
  ok: <CheckCircle2 size={10} />,
  tight: <AlertTriangle size={10} />,
  overflow: <AlertTriangle size={10} />,
  silence: <Pause size={10} />,
};

const healthLabels: Record<TimingHealth, string> = {
  ok: 'Timing OK',
  tight: 'Ajustado',
  overflow: 'Texto longo demais!',
  silence: 'Pausa longa',
};

export function StudioTab(props: StudioTabProps) {

  const {
    previewUrl, legendaUrl, timer, formatTime,
    videoScript, setVideoScript, transcriptionSegments,
    isTranscribing, isRefining, videoBlob,
    handleTranscribe, handleRefine, handleUpdateSegment,
    hasTranscription, setShowManualInput,
    editingSegmentIndex, setEditingSegmentIndex,
    editingSegmentText, setEditingSegmentText,
    refinementVersions, activeVersionIndex,
    handleSelectVersion, handleRestoreOriginal,
    playSegment, playSegmentBruto, stopSegment, playingSegment,
    ttsVoices, ttsSelectedVoiceId, ttsSetSelectedVoiceId, ttsGetVoice,
    segAudio, timelineEvents, healthApi, setVoiceName
  } = props;

  // Audio source for this tab
  const [audioSource, setAudioSource] = useState<'bruto' | 'preview' | 'premium'>(
    activeVersionIndex === -1 ? 'bruto' : 'preview'
  );
  const [premiumVoiceId, setPremiumVoiceId] = useState(VOICE_OPTIONS[0]?.name || '');
  const [showPreviewDrop, setShowPreviewDrop] = useState(false);
  const [showPremiumDrop, setShowPremiumDrop] = useState(false);

  // Sync premiumVoiceId selection with master recorder voiceName (fixes stale duration bugs)
  useEffect(() => {
    if (setVoiceName) {
      setVoiceName(premiumVoiceId);
    }
  }, [premiumVoiceId, setVoiceName]);
  const previewDropRef = useRef<HTMLDivElement>(null);
  const premiumDropRef = useRef<HTMLDivElement>(null);

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (previewDropRef.current && !previewDropRef.current.contains(e.target as Node)) setShowPreviewDrop(false);
      if (premiumDropRef.current && !premiumDropRef.current.contains(e.target as Node)) setShowPremiumDrop(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── Preview Player ── Only activated after transcription exists
  // When there's no transcription, we show a plain HTML5 video player.
  // This avoids the 60fps RAF sync loop when it's not needed.
  const previewPlayer = usePreviewPlayer(
    hasTranscription ? segAudio : undefined,
    hasTranscription ? ttsGetVoice : undefined,
    healthApi
  );

  // ── Configure original audio for "bruto" mode ──
  // Video is recorded mute (video track only); original mic audio is a separate file.
  useEffect(() => {
    // Priority: window global (set during finalize) > sessionStorage (survives reload)
    const globalUrl = (window as any).__ecadLastAudioUrl;
    if (globalUrl && previewPlayer.setOriginalAudioUrl) {
      const serveUrl = globalUrl.startsWith('http://') || globalUrl.startsWith('https://')
        ? globalUrl
        : globalUrl.startsWith('/ecad/')
          ? globalUrl.replace(/^\/ecad\//, '/api/v1/ecad/serve-local/')
          : globalUrl.startsWith('/api/')
            ? globalUrl
            : `/api/v1/ecad/serve-local/${globalUrl}`;
      previewPlayer.setOriginalAudioUrl(serveUrl);
      return;
    }
    // Fallback: sessionStorage
    try {
      const saved = sessionStorage.getItem('ecad_session');
      if (saved) {
        const session = JSON.parse(saved);
        if (session.audioUrl && previewPlayer.setOriginalAudioUrl) {
          const serveUrl = session.audioUrl.startsWith('http://') || session.audioUrl.startsWith('https://')
            ? session.audioUrl
            : session.audioUrl.startsWith('/api/')
              ? session.audioUrl
              : session.audioUrl.replace(/^\/ecad\//, '/api/v1/ecad/serve-local/');
          previewPlayer.setOriginalAudioUrl(serveUrl);
        }
      }
    } catch {}
  }, [previewPlayer.setOriginalAudioUrl]);

  // ── Sync voice refs with previewPlayer (multi-voice versioning) ──
  useEffect(() => {
    if (previewPlayer.setVoiceRefs) {
      previewPlayer.setVoiceRefs(premiumVoiceId, ttsSelectedVoiceId || '');
    }
  }, [premiumVoiceId, ttsSelectedVoiceId, previewPlayer.setVoiceRefs]);

  // ── Spacebar → play / pause ──
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.code !== 'Space') return;
      // Don't intercept when typing in inputs
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || (e.target as HTMLElement)?.isContentEditable) return;
      e.preventDefault();
      previewPlayer.togglePlay();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [previewPlayer.togglePlay]);

  // ── Timeline state ──
  const [selectedSegIdx, setSelectedSegIdx] = useState<number | null>(null);
  const [pxPerSec, setPxPerSec] = useState(28); // zoom level — 28 shows .5s sub-ticks by default
  const timelineRefs = useRef<Map<number, HTMLDivElement>>(new Map());

  // Native wheel listener (passive:false) so preventDefault works
  // Uses callback ref: binds handler when container mounts, unbinds on unmount
  const wheelHandlerRef = useRef<((e: WheelEvent) => void) | null>(null);
  if (!wheelHandlerRef.current) {
    wheelHandlerRef.current = (e: WheelEvent) => {
      const target = e.target as HTMLElement;
      const isTimelineTick = timelineRefs.current.size > 0 && (
        Array.from(timelineRefs.current.values()).some(el => el === target || el.contains(target))
      );
      if (!isTimelineTick) return;
      e.preventDefault();
      e.stopPropagation();
      setPxPerSec(prev => {
        const delta = e.deltaY > 0 ? -2 : 2;
        return Math.max(8, Math.min(60, prev + delta));
      });
    };
  }
  const timelineContainerCallbackRef = useRef<HTMLDivElement | null>(null);
  const setTimelineContainerRef = useCallback((el: HTMLDivElement | null) => {
    // Cleanup old
    if (timelineContainerCallbackRef.current && wheelHandlerRef.current) {
      timelineContainerCallbackRef.current.removeEventListener('wheel', wheelHandlerRef.current);
    }
    timelineContainerCallbackRef.current = el;
    // Bind new
    if (el && wheelHandlerRef.current) {
      el.addEventListener('wheel', wheelHandlerRef.current, { passive: false });
    }
  }, []);

  // ── Compute timing metadata for each segment ──
  const segmentsMeta = useMemo(() => {
    if (!transcriptionSegments || transcriptionSegments.length === 0) return [];
    // Filter out malformed segments (missing start/end/text)
    return transcriptionSegments
    .filter(seg => seg && typeof seg.start === 'string' && typeof seg.end === 'string' && typeof seg.text === 'string')
    .map((seg, idx, filteredArr) => {
      const startSec = parseVttTime(seg.start);
      const endSec = parseVttTime(seg.end);
      const durationSec = Math.max(0.1, endSec - startSec);

      // Pause BEFORE this segment (gap from previous segment's end)
      let pauseBeforeSec = 0;
      if (idx > 0) {
        const prevEnd = parseVttTime(filteredArr[idx - 1].end);
        pauseBeforeSec = Math.max(0, startSec - prevEnd);
      } else {
        pauseBeforeSec = startSec; // silence from video start
      }

      // Pause AFTER this segment (gap until next segment's start)
      let pauseAfterSec = 0;
      if (idx < filteredArr.length - 1) {
        const nextStart = parseVttTime(filteredArr[idx + 1].start);
        pauseAfterSec = Math.max(0, nextStart - endSec);
      }

      const wordCount = seg.text.trim().split(/\s+/).filter(Boolean).length;
      const speechEstimate = estimateSpeechDuration(seg.text);
      const wordsPerSec = wordCount / durationSec;
      const health = getTimingHealth(durationSec, speechEstimate, pauseAfterSec);

      return {
        ...seg,
        startSec,
        endSec,
        durationSec,
        pauseBeforeSec,
        pauseAfterSec,
        wordCount,
        speechEstimate,
        wordsPerSec,
        health,
      };
    });
  }, [transcriptionSegments]);

  // ── Global stats ──
  const totalWords = segmentsMeta.reduce((s, m) => s + m.wordCount, 0);
  const totalDuration = segmentsMeta.length > 0
    ? segmentsMeta[segmentsMeta.length - 1].endSec - segmentsMeta[0].startSec
    : 0;
  const totalPauses = segmentsMeta.reduce((s, m) => s + m.pauseBeforeSec, 0);
  const overflowCount = segmentsMeta.filter(m => m.health === 'overflow').length;

  // Sync segments data to preview player (must be after segmentsMeta useMemo)
  useEffect(() => {
    if (segmentsMeta.length > 0) {
      previewPlayer.setSegments(segmentsMeta.map(s => ({
        index: s.index, startSec: s.startSec, endSec: s.endSec, text: s.text
      })));
    }
  }, [segmentsMeta, previewPlayer.setSegments]);

  // Sync audioSource to preview player
  useEffect(() => {
    previewPlayer.setAudioSourceMode(audioSource);
  }, [audioSource, previewPlayer.setAudioSourceMode]);

  // Helper: format time M:SS
  const fmtPlayerTime = (sec: number) => {
    if (!sec || !isFinite(sec)) return '0:00';
    const m = Math.floor(sec / 60);
    const s = Math.floor(sec % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  // ══════════════════════════════════════════════════════
  // 🔧 DEBUG MODE — reveal components step-by-step
  // Set to false when debugging is complete
  // ══════════════════════════════════════════════════════
  const DEBUG_STEPS = false;
  const DEBUG_LABELS = [
    '🛑 Step 0 — Nenhum componente renderizado (apenas este banner)',
    '✅ Step 1 — Label do modo de áudio',
    '✅ Step 2 — Vídeo <video> element (player nativo)',
    '✅ Step 3 — TimelineMultiTrack (timeline multi-faixa)',
    '✅ Step 4 — Controles do player (play/pause, tempo, badge)',
    '✅ Step 5 — Tab bar de versões (Original, v1, v2...)',
    '✅ Step 6 — Header de áudio (Bruto / Preview / Premium)',
    '✅ Step 7 — Lista de segmentos (legendas editáveis)',
    '🟢 Step 8 — TUDO VISÍVEL (modo normal)',
  ];
  const [debugStep, setDebugStep] = useState(DEBUG_STEPS && hasTranscription ? 0 : 99);
  const ds = DEBUG_STEPS && hasTranscription ? debugStep : 99;

  return (
    <div className="recorder-modal-grid">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 0, height: '100%', overflow: 'hidden', minHeight: 0 }}>
        {/* ── Video Player ── */}
        {/* Before transcription: plain native video with browser controls */}
        {/* After transcription: advanced player with timeline + audio sync */}
        {!hasTranscription ? (
          /* ── Native Video Player (pre-transcription) ── */
          <>
            <div style={{
              background: '#000', borderRadius: 8, overflow: 'hidden',
              display: 'flex', justifyContent: 'center', width: '100%',
              aspectRatio: '16 / 9', maxHeight: '45%', flexShrink: 0,
              border: '1px solid #1e293b',
            }}>
              {previewUrl && (
                <video
                  src={previewUrl}
                  controls
                  style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                />
              )}
              {!previewUrl && (
                <div style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center',
                  justifyContent: 'center', color: '#475569', gap: 8, width: '100%'
                }}>
                  <FileVideo size={32} />
                  <span style={{ fontSize: '.8rem' }}>Nenhum vídeo gravado</span>
                </div>
              )}
            </div>
          </>
        ) : (
          /* ── Advanced Player (post-transcription) ── */
          <>
            {/* 🔧 DEBUG BANNER */}
            {DEBUG_STEPS && ds < 99 && (
              <div style={{
                background: 'linear-gradient(135deg, #1e1b4b, #0f172a)',
                border: '1.5px solid #6366f1',
                borderRadius: 8, padding: '10px 14px', marginBottom: 8,
                display: 'flex', flexDirection: 'column', gap: 8
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: '.72rem', fontWeight: 800, color: '#a5b4fc', letterSpacing: '.5px' }}>
                    🔧 MODO DEBUG — Revelando componentes
                  </span>
                  <span style={{ fontSize: '.6rem', color: '#6366f1', fontWeight: 700 }}>
                    {ds} / {DEBUG_LABELS.length - 1}
                  </span>
                </div>
                <div style={{
                  fontSize: '.7rem', color: '#e2e8f0', fontWeight: 600,
                  background: 'rgba(99, 102, 241, 0.1)', padding: '6px 10px',
                  borderRadius: 6, border: '1px solid rgba(99, 102, 241, 0.2)'
                }}>
                  {DEBUG_LABELS[ds] || DEBUG_LABELS[DEBUG_LABELS.length - 1]}
                </div>
                <div style={{
                  fontSize: '.6rem', color: '#94a3b8', lineHeight: 1.4
                }}>
                  {ds === 0 && 'Se a UI está estável agora, o problema está em um dos componentes. Clique "Próximo" para revelar um de cada vez.'}
                  {ds === 1 && 'Label leve renderizado. Se travou aqui, o problema é no hook usePreviewPlayer (RAF loop). Se está OK, próximo.'}
                  {ds === 2 && 'O elemento <video> com refs do previewPlayer foi adicionado. Se travou, o problema é no onLoadedMetadata ou onTimeUpdate. Se OK, próximo.'}
                  {ds === 3 && 'TimelineMultiTrack renderizado. Se travou, este componente com centenas de ticks DOM é o culpado. Se OK, próximo.'}
                  {ds === 4 && 'Controles do player (play/pause, tempo). Leve, raramente causa problemas. Próximo.'}
                  {ds === 5 && 'Tab bar de versões na coluna direita. Se travou, problema está nos botões/handlers das versões.'}
                  {ds === 6 && 'Header de seleção de áudio (Bruto/Preview/Premium com dropdowns). Se travou, verificar useEffect dos dropdowns.'}
                  {ds === 7 && 'Lista de segmentos com legendas editáveis. Se travou com muitos segmentos, problema é excesso de DOM.'}
                  {ds >= 8 && '✅ Todos os componentes renderizados — modo normal ativo. Se funcionar, o bug era de inicialização.'}
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button
                    onClick={() => setDebugStep(prev => Math.max(0, prev - 1))}
                    disabled={ds <= 0}
                    style={{
                      padding: '5px 12px', borderRadius: 6, fontSize: '.65rem', fontWeight: 700,
                      background: ds <= 0 ? '#1e293b' : 'rgba(99, 102, 241, 0.15)',
                      border: '1px solid rgba(99, 102, 241, 0.3)',
                      color: ds <= 0 ? '#475569' : '#a5b4fc',
                      cursor: ds <= 0 ? 'not-allowed' : 'pointer'
                    }}
                  >
                    ← Anterior
                  </button>
                  <button
                    onClick={() => setDebugStep(prev => prev + 1)}
                    style={{
                      padding: '5px 16px', borderRadius: 6, fontSize: '.65rem', fontWeight: 700,
                      background: 'rgba(34, 197, 94, 0.15)',
                      border: '1px solid rgba(34, 197, 94, 0.3)',
                      color: '#4ade80', cursor: 'pointer'
                    }}
                  >
                    Próximo →
                  </button>
                  <button
                    onClick={() => setDebugStep(99)}
                    style={{
                      padding: '5px 12px', borderRadius: 6, fontSize: '.65rem', fontWeight: 700,
                      background: 'rgba(239, 68, 68, 0.1)',
                      border: '1px solid rgba(239, 68, 68, 0.3)',
                      color: '#f87171', cursor: 'pointer', marginLeft: 'auto'
                    }}
                  >
                    Pular Debug
                  </button>
                </div>
              </div>
            )}

            {/* Step 1: Label with mode indicator */}
            {ds >= 1 && (
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '6px 0'
              }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '.78rem', fontWeight: 600, color: '#38bdf8' }}>
                  <FileVideo size={14} />
                  {audioSource === 'bruto' ? 'Vídeo Original (Bruto)'
                    : audioSource === 'preview' ? 'Preview — TTS'
                    : 'Preview — Gemini AI'}
                </label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  {audioSource !== 'bruto' && previewPlayer.activeSegIdx !== null && (
                    <span style={{
                      fontSize: '.56rem', fontWeight: 700,
                      color: audioSource === 'preview' ? '#facc15' : '#4ade80',
                      background: audioSource === 'preview' ? 'rgba(234,179,8,0.1)' : 'rgba(34,197,94,0.1)',
                      padding: '2px 6px', borderRadius: 3
                    }}>
                      Seg #{previewPlayer.activeSegIdx + 1}
                    </span>
                  )}
                  {previewPlayer.isFrozen && (
                    <span style={{
                      fontSize: '.56rem', fontWeight: 800,
                      color: '#38bdf8',
                      background: 'rgba(56, 189, 248, 0.15)',
                      padding: '2px 6px', borderRadius: 3,
                      animation: 'pulse 1.5s ease-in-out infinite',
                      display: 'flex', alignItems: 'center', gap: 3
                    }}>
                      ❄️ FREEZE — áudio tocando
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Step 2: Video container */}
            {ds >= 2 && (
              <div style={{
                background: '#000', borderRadius: '8px 8px 0 0', overflow: 'hidden',
                display: 'flex', justifyContent: 'center', width: '100%',
                aspectRatio: '16 / 9', maxHeight: '45%', flexShrink: 0,
                border: '1px solid #1e293b', borderBottom: 'none',
                position: 'relative', cursor: previewPlayer.videoError ? 'default' : 'pointer'
              }}
                onClick={previewPlayer.videoError ? undefined : previewPlayer.togglePlay}
              >
                {previewUrl && !previewPlayer.videoError && (
                  <video
                    ref={previewPlayer.videoRef}
                    key={legendaUrl || 'no-caption-bruto'}
                    src={previewUrl}
                    muted={audioSource !== 'bruto'}
                    crossOrigin="anonymous"
                    preload="metadata"
                    onLoadedMetadata={previewPlayer.onLoadedMetadata}
                    onEnded={previewPlayer.onEnded}
                    onTimeUpdate={previewPlayer.onTimeUpdate}
                    onError={previewPlayer.onError}
                    onCanPlay={previewPlayer.clearVideoError}
                    style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                  >
                    {legendaUrl && (
                      <track src={legendaUrl} kind="subtitles" srcLang="pt" label="Português" default />
                    )}
                  </video>
                )}

                {/* Error overlay */}
                {previewPlayer.videoError && (
                  <div style={{
                    position: 'absolute', inset: 0,
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                    background: 'rgba(0,0,0,0.85)', padding: 20, gap: 12, zIndex: 10
                  }}>
                    <AlertTriangle size={32} style={{ color: '#f59e0b' }} />
                    <span style={{ color: '#f59e0b', fontSize: '.85rem', fontWeight: 600, textAlign: 'center' }}>
                      {previewPlayer.videoError}
                    </span>
                    <span style={{ color: '#94a3b8', fontSize: '.72rem', textAlign: 'center', maxWidth: 280 }}>
                      O vídeo foi salvo no servidor com sucesso. A pré-visualização pode não funcionar com alguns formatos.
                    </span>
                    <button
                      onClick={(e) => { e.stopPropagation(); previewPlayer.clearVideoError(); }}
                      style={{
                        marginTop: 4, padding: '6px 16px', borderRadius: 6, border: '1px solid #475569',
                        background: '#1e293b', color: '#e2e8f0', fontSize: '.75rem', cursor: 'pointer'
                      }}
                    >
                      Tentar novamente
                    </button>
                  </div>
                )}

                {/* Play overlay */}
                {!previewPlayer.isPlaying && previewUrl && !previewPlayer.videoError && (
                  <div style={{
                    position: 'absolute', inset: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: 'rgba(0,0,0,0.35)', transition: 'opacity 0.2s'
                  }}>
                    <div style={{
                      width: 48, height: 48, borderRadius: '50%',
                      background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(4px)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      border: '2px solid rgba(255,255,255,0.3)'
                    }}>
                      <Play size={22} style={{ color: '#fff', marginLeft: 2 }} />
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Step 3: Multi-track timeline */}
            {ds >= 3 && (
              <TimelineMultiTrack
                duration={previewPlayer.duration}
                currentTime={previewPlayer.currentTime}
                segmentsMeta={segmentsMeta}
                timelineEvents={timelineEvents || []}
                audioSource={audioSource}
                segAudio={segAudio}
                onSeek={previewPlayer.seek}
                pxPerSec={pxPerSec}
                activeSegIdx={previewPlayer.activeSegIdx}
                voiceName={audioSource === 'preview' ? (ttsSelectedVoiceId || '') : premiumVoiceId}
              />
            )}

            {/* Step 4: Controls row */}
            {ds >= 4 && (
              <div style={{
                background: '#0f172a', borderRadius: '0 0 8px 8px',
                border: '1px solid #1e293b', borderTop: 'none',
                padding: '4px 10px', display: 'flex', alignItems: 'center', gap: 8
              }}>
                <button
                  onClick={(e) => { e.stopPropagation(); previewPlayer.togglePlay(); }}
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                    color: '#f8fafc', display: 'flex', alignItems: 'center'
                  }}
                >
                  {previewPlayer.isPlaying ? <Pause size={14} /> : <Play size={14} style={{ marginLeft: 1 }} />}
                </button>
                <span style={{ fontSize: '.62rem', color: '#94a3b8', fontVariantNumeric: 'tabular-nums', minWidth: 70 }}>
                  {fmtPlayerTime(previewPlayer.currentTime)} / {fmtPlayerTime(previewPlayer.duration)}
                </span>
                {/* Mode badge */}
                <span style={{
                  marginLeft: 'auto', fontSize: '.5rem', fontWeight: 700, letterSpacing: '.5px',
                  color: audioSource === 'bruto' ? '#64748b'
                    : audioSource === 'preview' ? '#eab308' : '#22c55e',
                  textTransform: 'uppercase'
                }}>
                  {audioSource === 'bruto' ? '🔊 BRUTO'
                    : audioSource === 'preview' ? '🔉 PREVIEW'
                    : '👑 PREMIUM'}
                </span>
              </div>
            )}

            {/* ── Botão Renderizar ── */}
            {ds >= 6 && audioSource !== 'preview' && segmentsMeta.length > 0 && (
              <button
                onClick={() => props.onRenderStart?.(
                  audioSource as 'bruto' | 'premium',
                  audioSource === 'premium' ? premiumVoiceId : ''
                )}
                disabled={props.isRendering || !previewPlayer.duration}
                style={{
                  width: '100%', padding: '10px 16px', marginTop: 6,
                  borderRadius: 8, border: 'none',
                  background: props.isRendering
                    ? 'rgba(139, 92, 246, 0.15)'
                    : audioSource === 'bruto'
                      ? 'linear-gradient(135deg, #475569, #334155)'
                      : 'linear-gradient(135deg, #22c55e, #16a34a)',
                  color: '#fff', fontSize: '.72rem', fontWeight: 800,
                  cursor: (props.isRendering || !previewPlayer.duration) ? 'not-allowed' : 'pointer',
                  opacity: (props.isRendering || !previewPlayer.duration) ? 0.5 : 1,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  transition: 'all 0.3s',
                  boxShadow: !props.isRendering ? '0 3px 12px rgba(34,197,94,0.3)' : 'none',
                  flexShrink: 0,
                }}
              >
                {props.isRendering ? (
                  <><Loader2 size={14} className="animate-spin" /> Renderizando...</>
                ) : (
                  <><Clapperboard size={14} /> Renderizar {audioSource === 'bruto' ? 'Original' : 'Premium'}</>
                )}
              </button>
            )}
          </>
        )}

        {/* ── Error Log Panel (PreviewPlayer) — inside left column, below video ── */}
        {previewPlayer.errorLog.length > 0 && (
          <div style={{
            background: '#1a0a0a', border: '1px solid #7f1d1d', borderRadius: 8,
            padding: '6px 10px', marginTop: 6, maxHeight: 120, overflowY: 'auto',
            flexShrink: 0,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
              <span style={{ fontSize: '.6rem', fontWeight: 700, color: '#f87171', textTransform: 'uppercase', letterSpacing: '.5px' }}>
                ⚠ Log de Erros ({previewPlayer.errorLog.length})
              </span>
              <button
                onClick={() => previewPlayer.clearErrorLog()}
                style={{
                  background: 'none', border: '1px solid #7f1d1d', borderRadius: 4,
                  color: '#f87171', fontSize: '.55rem', padding: '1px 6px', cursor: 'pointer',
                }}
              >
                Limpar
              </button>
            </div>
            {previewPlayer.errorLog.slice(0, 10).map((err) => (
              <div key={err.id} style={{
                fontSize: '.58rem', color: '#fca5a5', padding: '2px 0',
                borderTop: '1px solid rgba(127,29,29,0.3)', lineHeight: 1.4,
              }}>
                <span style={{ color: '#6b7280', marginRight: 4 }}>{err.timestamp}</span>
                <span style={{
                  display: 'inline-block', padding: '0 4px', borderRadius: 3, marginRight: 4,
                  fontSize: '.5rem', fontWeight: 700, textTransform: 'uppercase',
                  background: err.source === 'video' ? '#7f1d1d'
                    : err.source === 'audio' ? '#713f12'
                    : err.source === 'tts' ? '#4a1d96'
                    : err.source === 'play' ? '#1e3a5f'
                    : '#374151',
                  color: '#fef2f2',
                }}>
                  {err.source}
                </span>
                <span>{err.message}</span>
                {err.details && (
                  <div style={{ color: '#6b7280', fontSize: '.52rem', paddingLeft: 8, marginTop: 1 }}>
                    ↳ {err.details}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      {hasTranscription ? (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>

          {/* Step 5: Integrated tab bar: Original | v1 | v2 | Aprimorar */}
          {ds >= 5 && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 0,
            background: '#0f172a', borderRadius: '8px 8px 0 0',
            border: '1px solid #334155', borderBottom: 'none',
            padding: '0', flexShrink: 0
          }}>
            {/* Version pills */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 0, flex: 1 }}>
              <button
                onClick={() => handleSelectVersion(-1)}
                style={{
                  padding: '7px 14px', border: 'none', borderRight: '1px solid #1e293b',
                  background: activeVersionIndex === -1 ? 'rgba(56, 189, 248, 0.1)' : 'transparent',
                  color: activeVersionIndex === -1 ? '#38bdf8' : '#64748b',
                  fontSize: '.68rem', fontWeight: 700, cursor: 'pointer',
                  transition: 'all 0.2s', borderRadius: '8px 0 0 0',
                  position: 'relative'
                }}
              >
                Original
                {activeVersionIndex === -1 && (
                  <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 2, background: '#38bdf8', boxShadow: '0 0 6px #38bdf8' }} />
                )}
              </button>
              {refinementVersions.map((ver, idx) => (
                <button
                  key={idx}
                  onClick={() => handleSelectVersion(idx)}
                  title={`Gerado em ${new Date(ver.timestamp).toLocaleTimeString('pt-BR')}`}
                  style={{
                    padding: '7px 12px', border: 'none', borderRight: '1px solid #1e293b',
                    background: activeVersionIndex === idx ? 'rgba(139, 92, 246, 0.1)' : 'transparent',
                    color: activeVersionIndex === idx ? '#a78bfa' : '#64748b',
                    fontSize: '.68rem', fontWeight: 700, cursor: 'pointer',
                    transition: 'all 0.2s', position: 'relative'
                  }}
                >
                  v{idx + 1}
                  {activeVersionIndex === idx && (
                    <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 2, background: '#a78bfa', boxShadow: '0 0 6px #a78bfa' }} />
                  )}
                </button>
              ))}
            </div>

            {/* Restore button */}
            {activeVersionIndex !== -1 && (
              <button
                onClick={handleRestoreOriginal}
                style={{
                  padding: '5px 8px', border: 'none', borderRight: '1px solid #1e293b',
                  background: 'transparent', color: '#f87171',
                  fontSize: '.6rem', fontWeight: 600, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 3,
                  transition: 'all 0.2s'
                }}
              >
                <RotateCcw size={9} />
              </button>
            )}

            {/* Aprimorar button — integrated in tab bar */}
            <button
              onClick={handleRefine}
              disabled={isRefining || !videoScript}
              style={{
                padding: '7px 12px', border: 'none',
                background: isRefining ? 'rgba(139, 92, 246, 0.15)' : 'rgba(56, 189, 248, 0.06)',
                color: (isRefining || !videoScript) ? '#475569' : '#38bdf8',
                fontSize: '.66rem', fontWeight: 700, cursor: (!videoScript || isRefining) ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center', gap: 4,
                transition: 'all 0.2s', borderRadius: '0 8px 0 0',
                marginLeft: 'auto', whiteSpace: 'nowrap'
              }}
            >
              {isRefining ? <Loader2 size={10} className="animate-spin" /> : <Sparkles size={10} />}
              Aprimorar
            </button>
          </div>
          )}

          {/* Step 6: Tab Audio Config Header */}
          {ds >= 6 && (() => {
            // Friendly names for TTS preview voices
            const previewVoiceLabel = (v: TtsVoiceOption) => {
              const n = v.name.toLowerCase();
              if (n.includes('daniel')) return { label: 'Daniel', gender: 'M' as const };
              if (n.includes('maria')) return { label: 'Maria', gender: 'F' as const };
              if (n.includes('francisca')) return { label: 'Francisca', gender: 'F' as const };
              if (n.includes('antonio')) return { label: 'Antônio', gender: 'M' as const };
              if (n.includes('helia') || n.includes('hélia')) return { label: 'Hélia', gender: 'F' as const };
              if (n.includes('duarte')) return { label: 'Duarte', gender: 'M' as const };
              if (n.includes('google') && n.includes('brasil')) return { label: 'Google BR', gender: 'F' as const };
              if (n.includes('google')) return { label: 'Google PT', gender: 'F' as const };
              return { label: v.name.split(' ')[0], gender: 'M' as const };
            };

            const selectedPreviewVoice = ttsVoices?.find(v => v.id === ttsSelectedVoiceId);
            const previewLabel = selectedPreviewVoice ? previewVoiceLabel(selectedPreviewVoice).label : 'Preview';

            const selectedPremiumVoice = VOICE_OPTIONS.find(v => v.name === premiumVoiceId);
            const premiumLabel = selectedPremiumVoice
              ? `${selectedPremiumVoice.name} (${selectedPremiumVoice.desc.split(' ')[1]?.replace('.', '') || 'IA'})`
              : 'Premium';

            const premiumMale = VOICE_OPTIONS.filter(v => v.gender === 'male');
            const premiumFemale = VOICE_OPTIONS.filter(v => v.gender === 'female');

            // Active color logic
            const activeColor = audioSource === 'bruto' ? '#94a3b8'
              : audioSource === 'preview' ? '#eab308'
              : '#22c55e';

            return (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '3px 10px', background: '#0a0a0a',
                border: '1px solid #334155', borderTop: 'none',
                fontSize: '.6rem', flexShrink: 0
              }}>
                {/* Bruto button */}
                <button
                  onClick={() => setAudioSource('bruto')}
                  style={{
                    padding: '3px 8px', borderRadius: 4, cursor: 'pointer',
                    border: audioSource === 'bruto' ? '1px solid rgba(148, 163, 184, 0.4)' : '1px solid transparent',
                    background: audioSource === 'bruto' ? 'rgba(148, 163, 184, 0.1)' : 'transparent',
                    color: audioSource === 'bruto' ? '#cbd5e1' : '#475569',
                    fontSize: '.58rem', fontWeight: 700,
                    display: 'flex', alignItems: 'center', gap: 3,
                    transition: 'all 0.15s'
                  }}
                >
                  <Volume2 size={9} /> Bruto
                </button>

                {/* Preview dropdown */}
                <div ref={previewDropRef} style={{ position: 'relative' }}>
                  <button
                    onClick={() => { setShowPreviewDrop(!showPreviewDrop); setShowPremiumDrop(false); if (audioSource !== 'preview') setAudioSource('preview'); }}
                    style={{
                      padding: '3px 8px', borderRadius: 4, cursor: 'pointer',
                      border: audioSource === 'preview' ? '1px solid rgba(234, 179, 8, 0.4)' : '1px solid transparent',
                      background: audioSource === 'preview' ? 'rgba(234, 179, 8, 0.1)' : 'transparent',
                      color: audioSource === 'preview' ? '#facc15' : '#475569',
                      fontSize: '.58rem', fontWeight: 700,
                      display: 'flex', alignItems: 'center', gap: 3,
                      transition: 'all 0.15s'
                    }}
                  >
                    <Volume1 size={9} /> {previewLabel} <ChevronDown size={8} />
                  </button>
                  {showPreviewDrop && ttsVoices && ttsVoices.length > 0 && (
                    <div style={{
                      position: 'absolute', top: '100%', left: 0, marginTop: 2,
                      background: '#1e293b', border: '1px solid #eab308',
                      borderRadius: 6, padding: '4px 0', zIndex: 100,
                      minWidth: 160, boxShadow: '0 4px 16px rgba(0,0,0,0.5)'
                    }}>
                      {ttsVoices.map(v => {
                        const info = previewVoiceLabel(v);
                        const isActive = v.id === ttsSelectedVoiceId;
                        return (
                          <button
                            key={v.id}
                            onClick={() => { ttsSetSelectedVoiceId?.(v.id); setShowPreviewDrop(false); setAudioSource('preview'); }}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 6, width: '100%',
                              padding: '5px 10px', border: 'none', cursor: 'pointer',
                              background: isActive ? 'rgba(234, 179, 8, 0.12)' : 'transparent',
                              color: isActive ? '#facc15' : '#cbd5e1',
                              fontSize: '.6rem', fontWeight: isActive ? 700 : 500,
                              textAlign: 'left', transition: 'background 0.1s'
                            }}
                          >
                            <span style={{ color: info.gender === 'F' ? '#f472b6' : '#60a5fa', fontSize: '.65rem' }}>
                              {info.gender === 'F' ? '♀' : '♂'}
                            </span>
                            {info.label}
                            <span style={{ color: '#475569', fontSize: '.5rem', marginLeft: 'auto' }}>{v.lang}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Premium dropdown */}
                <div ref={premiumDropRef} style={{ position: 'relative' }}>
                  <button
                    onClick={() => { setShowPremiumDrop(!showPremiumDrop); setShowPreviewDrop(false); if (audioSource !== 'premium') setAudioSource('premium'); }}
                    style={{
                      padding: '3px 8px', borderRadius: 4, cursor: 'pointer',
                      border: audioSource === 'premium' ? '1px solid rgba(34, 197, 94, 0.4)' : '1px solid transparent',
                      background: audioSource === 'premium' ? 'rgba(34, 197, 94, 0.1)' : 'transparent',
                      color: audioSource === 'premium' ? '#4ade80' : '#475569',
                      fontSize: '.58rem', fontWeight: 700,
                      display: 'flex', alignItems: 'center', gap: 3,
                      transition: 'all 0.15s'
                    }}
                  >
                    <Crown size={9} /> {selectedPremiumVoice?.name || 'Premium'} <ChevronDown size={8} />
                  </button>
                  {showPremiumDrop && (
                    <div style={{
                      position: 'absolute', top: '100%', left: 0, marginTop: 2,
                      background: '#1e293b', border: '1px solid #22c55e',
                      borderRadius: 6, padding: '4px 0', zIndex: 100,
                      minWidth: 190, boxShadow: '0 4px 16px rgba(0,0,0,0.5)'
                    }}>
                      {/* Masculino group */}
                      <div style={{ padding: '3px 10px', color: '#60a5fa', fontSize: '.5rem', fontWeight: 700, letterSpacing: '.5px' }}>♂ MASCULINO</div>
                      {premiumMale.map(v => {
                        const isActive = v.name === premiumVoiceId;
                        const tone = v.label.match(/\((.+)\)/)?.[1] || '';
                        return (
                          <button
                            key={v.id}
                            onClick={() => { setPremiumVoiceId(v.name); setShowPremiumDrop(false); setAudioSource('premium'); }}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 6, width: '100%',
                              padding: '5px 10px', border: 'none', cursor: 'pointer',
                              background: isActive ? 'rgba(34, 197, 94, 0.12)' : 'transparent',
                              color: isActive ? '#4ade80' : '#cbd5e1',
                              fontSize: '.6rem', fontWeight: isActive ? 700 : 500,
                              textAlign: 'left', transition: 'background 0.1s'
                            }}
                          >
                            {v.name}
                            {tone && <span style={{ color: '#475569', fontSize: '.48rem' }}>({tone})</span>}
                          </button>
                        );
                      })}
                      {/* Feminino group */}
                      <div style={{ padding: '3px 10px', color: '#f472b6', fontSize: '.5rem', fontWeight: 700, letterSpacing: '.5px', marginTop: 2, borderTop: '1px solid #334155' }}>♀ FEMININO</div>
                      {premiumFemale.map(v => {
                        const isActive = v.name === premiumVoiceId;
                        const tone = v.label.match(/\((.+)\)/)?.[1] || '';
                        return (
                          <button
                            key={v.id}
                            onClick={() => { setPremiumVoiceId(v.name); setShowPremiumDrop(false); setAudioSource('premium'); }}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 6, width: '100%',
                              padding: '5px 10px', border: 'none', cursor: 'pointer',
                              background: isActive ? 'rgba(34, 197, 94, 0.12)' : 'transparent',
                              color: isActive ? '#4ade80' : '#cbd5e1',
                              fontSize: '.6rem', fontWeight: isActive ? 700 : 500,
                              textAlign: 'left', transition: 'background 0.1s'
                            }}
                          >
                            {v.name}
                            {tone && <span style={{ color: '#475569', fontSize: '.48rem' }}>({tone})</span>}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Gerar Todos — hides when all generated for active source */}
                {audioSource !== 'bruto' && segmentsMeta.length > 0
                  && !segAudio?.allGenerated(segmentsMeta.map(s => ({ index: s.index, text: s.text })), audioSource as 'preview' | 'premium', audioSource === 'premium' ? premiumVoiceId : (ttsSelectedVoiceId || ''))
                  && (
                  <button
                    onClick={() => {
                      if (segAudio?.isGeneratingAll) {
                        segAudio.cancelGenerateAll();
                      } else if (audioSource === 'preview') {
                        segAudio?.generateAllPending(
                          segmentsMeta.map(s => ({ index: s.index, text: s.text, startSec: s.startSec, endSec: s.endSec })),
                          'preview', ttsSelectedVoiceId || '', ttsGetVoice
                        );
                      } else if (audioSource === 'premium') {
                        segAudio?.generateAllPending(
                          segmentsMeta.map(s => ({ index: s.index, text: s.text, startSec: s.startSec, endSec: s.endSec })),
                          'premium', premiumVoiceId
                        );
                      }
                    }}
                    style={{
                      marginLeft: 'auto',
                      padding: '3px 10px', borderRadius: 4,
                      border: `1px solid ${activeColor}33`,
                      background: `${activeColor}18`,
                      color: activeColor,
                      fontSize: '.58rem', fontWeight: 700, cursor: 'pointer',
                      display: 'flex', alignItems: 'center', gap: 3,
                      transition: 'all 0.15s'
                    }}
                  >
                    {segAudio?.isGeneratingAll ? <><StopCircle size={9} /> Parar</> : <><Zap size={9} /> Gerar Todos</>}
                  </button>
                )}
              </div>
            );
          })()}

          {/* Step 7: Segments list with proportional timeline */}
          {ds >= 7 && segmentsMeta.length > 0 ? (
            <div
              ref={setTimelineContainerRef}
              style={{
                flex: 1, overflowY: 'auto', background: '#1e293b',
                border: '1px solid #334155', borderTop: 'none', borderBottom: 'none',
                padding: '0',
                display: 'flex', flexDirection: 'column', gap: 0
              }}
            >
              {segmentsMeta.map((seg, i) => {
                const segMinH = Math.max(seg.durationSec * pxPerSec, 44);
                const pauseMinH = seg.pauseBeforeSec > 0.05 ? Math.max(seg.pauseBeforeSec * pxPerSec, 18) : 0;
                const isSelected = selectedSegIdx === seg.index;

                // Adaptive tick interval based on zoom
                const tickStep = pxPerSec >= 36 ? 0.2
                  : pxPerSec >= 22 ? 0.5
                  : pxPerSec >= 12 ? 1
                  : 2;

                // Format tick label: full "MM:SS" for whole seconds, ".N" for fractions
                const fmtTick = (sec: number, prevWholeSec: number | null): string => {
                  const wholeSec = Math.floor(sec);
                  const frac = sec - wholeSec;
                  const mm = String(Math.floor(wholeSec / 60)).padStart(2, '0');
                  const ss = String(wholeSec % 60).padStart(2, '0');
                  // If it's a whole second and different from previous shown whole second
                  if (Math.abs(frac) < 0.01) {
                    if (prevWholeSec === wholeSec) return ''; // skip duplicate
                    return `${mm}:${ss}`;
                  }
                  // Sub-second: show ".N"
                  return `.${Math.round(frac * 10)}`;
                };

                // Generate ticks for this segment
                const segStartSec = seg.startSec;
                const segEndSec = seg.endSec;
                const ticks: { sec: number; label: string; isMajor: boolean }[] = [];
                let lastWhole: number | null = null;

                // Start from the first aligned tick at or after segStartSec
                const firstAligned = Math.ceil(segStartSec / tickStep) * tickStep;
                for (let t = firstAligned; t < segEndSec; t = Math.round((t + tickStep) * 100) / 100) {
                  const label = fmtTick(t, lastWhole);
                  const isMajor = Math.abs(t - Math.round(t)) < 0.01;
                  if (label) {
                    ticks.push({ sec: t, label, isMajor });
                    if (isMajor) lastWhole = Math.floor(t);
                  }
                }
                // Ensure at least the start label
                if (ticks.length === 0) {
                  const mm = String(Math.floor(segStartSec / 60)).padStart(2, '0');
                  const ss = String(Math.floor(segStartSec % 60)).padStart(2, '0');
                  ticks.push({ sec: segStartSec, label: `${mm}:${ss}`, isMajor: true });
                }

                // Pause ticks (same adaptive logic)
                const pauseTicks: { sec: number; label: string; isMajor: boolean }[] = [];
                if (seg.pauseBeforeSec > 0.05 && i > 0) {
                  const pauseStart = segmentsMeta[i - 1].endSec;
                  let pLastWhole: number | null = null;
                  const pFirstAligned = Math.ceil(pauseStart / tickStep) * tickStep;
                  for (let t = pFirstAligned; t < segStartSec; t = Math.round((t + tickStep) * 100) / 100) {
                    const label = fmtTick(t, pLastWhole);
                    const isMajor = Math.abs(t - Math.round(t)) < 0.01;
                    if (label) {
                      pauseTicks.push({ sec: t, label, isMajor });
                      if (isMajor) pLastWhole = Math.floor(t);
                    }
                  }
                }

                return (
                  <React.Fragment key={seg.index}>
                    {/* ── Pause row ── */}
                    {seg.pauseBeforeSec > 0.05 && (
                      <div style={{
                        display: 'flex', flexDirection: 'row',
                        minHeight: pauseMinH
                      }}>
                        {/* Timeline: pause */}
                        <div
                          style={{
                            width: 48, minWidth: 48, flexShrink: 0,
                            display: 'flex', flexDirection: 'column',
                            alignItems: 'flex-end', paddingRight: 5,
                            borderRight: '1px solid rgba(226, 232, 240, 0.08)',
                            position: 'relative', justifyContent: 'space-around',
                            background: '#0a0a0a'
                          }}
                        >
                          {pauseTicks.map((tk, ti) => (
                            <span key={ti} style={{
                              fontSize: tk.isMajor ? '.58rem' : '.5rem',
                              fontFamily: 'monospace',
                              color: tk.isMajor ? 'rgba(148, 163, 184, 0.6)' : 'rgba(148, 163, 184, 0.3)',
                              lineHeight: 1, whiteSpace: 'nowrap',
                              fontWeight: tk.isMajor ? 700 : 400
                            }}>
                              {tk.label}
                            </span>
                          ))}
                        </div>
                        {/* Pause content */}
                        <div style={{
                          flex: 1, display: 'flex', alignItems: 'center',
                          padding: '0 8px 0 6px'
                        }}>
                          <div style={{ flex: 1, height: '1px', background: 'rgba(226, 232, 240, 0.1)' }} />
                          <span style={{
                            display: 'flex', alignItems: 'center', gap: 3,
                            fontSize: '.56rem', fontFamily: 'monospace', fontWeight: 600,
                            color: 'rgba(226, 232, 240, 0.4)',
                            padding: '1px 6px', whiteSpace: 'nowrap'
                          }}>
                            <Pause size={6} /> {seg.pauseBeforeSec.toFixed(1)}s
                          </span>
                          <div style={{ flex: 1, height: '1px', background: 'rgba(226, 232, 240, 0.1)' }} />
                        </div>
                      </div>
                    )}

                    {/* ── Segment row ── */}
                    <div style={{
                      display: 'flex', flexDirection: 'row',
                      minHeight: segMinH
                    }}>
                      {/* Timeline: segment ticks */}
                      <div
                        ref={(el) => { if (el) timelineRefs.current.set(seg.index, el); else timelineRefs.current.delete(seg.index); }}
                        onClick={() => setSelectedSegIdx(isSelected ? null : seg.index)}
                        style={{
                          width: 48, minWidth: 48, flexShrink: 0,
                          display: 'flex', flexDirection: 'column',
                          alignItems: 'flex-end', paddingRight: 5,
                          borderRight: `1px solid ${isSelected ? 'rgba(56, 189, 248, 0.5)' : 'rgba(56, 189, 248, 0.12)'}`,
                          cursor: 'pointer', position: 'relative',
                          justifyContent: 'space-between', paddingTop: 3, paddingBottom: 3,
                          transition: 'border-color 0.2s',
                          background: '#0a0a0a'
                        }}
                        title="Clique para selecionar • Scroll para zoom"
                      >
                        {ticks.map((tk, ti) => (
                          <span key={ti} style={{
                            fontSize: tk.isMajor ? '.58rem' : '.5rem',
                            fontFamily: 'monospace',
                            fontWeight: tk.isMajor ? 700 : 400,
                            color: isSelected
                              ? (tk.isMajor ? '#7dd3fc' : 'rgba(125, 211, 252, 0.5)')
                              : (tk.isMajor ? '#94a3b8' : 'rgba(148, 163, 184, 0.45)'),
                            lineHeight: 1, whiteSpace: 'nowrap',
                            transition: 'color 0.2s'
                          }}>
                            {tk.label}
                          </span>
                        ))}
                        {ticks.length === 0 && (
                          <span style={{ fontSize: '.58rem', fontFamily: 'monospace', color: '#94a3b8' }}>
                            {String(Math.floor(segStartSec / 60)).padStart(2, '0')}:{String(Math.floor(segStartSec % 60)).padStart(2, '0')}
                          </span>
                        )}
                      </div>

                      {/* Card */}
                      <div style={{
                        flex: 1, padding: '2px 8px 2px 6px',
                        display: 'flex', flexDirection: 'column'
                      }}>
                        <div
                          style={{
                            background: '#0f172a', borderRadius: 5,
                            border: isSelected ? '1px solid rgba(56, 189, 248, 0.6)' : '1px solid #334155',
                            boxShadow: isSelected ? '0 0 8px rgba(56, 189, 248, 0.15)' : 'none',
                            padding: '6px 8px', display: 'flex', flexDirection: 'column', gap: 2,
                            transition: 'all 0.2s', flex: 1, justifyContent: 'flex-start'
                          }}
                        >
                          {/* Line 1: #index + duration + overflow badge ... [play ▶] */}
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '.65rem', fontWeight: 700 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <span style={{ color: '#38bdf8' }}>#{seg.index + 1}</span>
                              <span style={{
                                display: 'flex', alignItems: 'center', gap: 2,
                                background: 'rgba(56, 189, 248, 0.1)', color: '#38bdf8',
                                padding: '0px 5px', borderRadius: 3, fontFamily: 'monospace',
                                fontSize: '.58rem', fontWeight: 600
                              }}>
                                <Timer size={7} /> {formatDuration(seg.durationSec)}
                              </span>
                              {/* Overflow/tight indicator */}
                              {(seg.health === 'overflow' || seg.health === 'tight') && (() => {
                                const excessSec = seg.speechEstimate - seg.durationSec;
                                const excessStr = `${excessSec.toFixed(1)}s`;
                                const tooltipText = seg.health === 'overflow'
                                  ? `Texto longo demais!\nTTS estimado: ~${formatDuration(seg.speechEstimate)}\nJanela disponível: ${formatDuration(seg.durationSec)}\nExcede em +${excessStr}\n\nReduca o texto para caber na janela de tempo.`
                                  : `Texto ajustado (quase no limite)\nTTS estimado: ~${formatDuration(seg.speechEstimate)}\nJanela disponível: ${formatDuration(seg.durationSec)}\nMargem: apenas ${excessStr}\n\nConsidere reduzir levemente o texto.`;
                                return (
                                  <span
                                    title={tooltipText}
                                    style={{
                                      display: 'flex', alignItems: 'center', gap: 3,
                                      color: healthColors[seg.health],
                                      fontSize: '.58rem', fontWeight: 800, cursor: 'help',
                                      background: seg.health === 'overflow' ? 'rgba(239, 68, 68, 0.1)' : 'rgba(245, 158, 11, 0.1)',
                                      padding: '0px 5px', borderRadius: 3,
                                      border: `1px solid ${seg.health === 'overflow' ? 'rgba(239, 68, 68, 0.25)' : 'rgba(245, 158, 11, 0.25)'}`,
                                      fontFamily: 'monospace'
                                    }}
                                  >
                                    <AlertTriangle size={8} />
                                    +{excessStr}
                                  </span>
                                );
                              })()}
                              {/* Resolved indicator */}
                              {seg.resolvedAddedSec !== undefined && seg.resolvedAddedSec > 0 && seg.health !== 'overflow' && seg.health !== 'tight' && (
                                <span
                                  title={`Overflow resolvido! Tempo estendido em +${seg.resolvedAddedSec.toFixed(1)}s para acomodar a fala.`}
                                  style={{
                                    display: 'flex', alignItems: 'center', gap: 3,
                                    color: '#22c55e',
                                    fontSize: '.58rem', fontWeight: 800, cursor: 'help',
                                    background: 'rgba(34, 197, 94, 0.1)',
                                    padding: '0px 5px', borderRadius: 3,
                                    border: '1px solid rgba(34, 197, 94, 0.25)',
                                    fontFamily: 'monospace'
                                  }}
                                >
                                  <CheckCircle2 size={8} />
                                  +{seg.resolvedAddedSec.toFixed(1)}s
                                </span>
                              )}
                              {seg.health === 'silence' && (
                                <span
                                  title={`Pausa longa de ${formatDuration(seg.pauseAfterSec)} após este trecho`}
                                  style={{
                                    display: 'flex', alignItems: 'center', gap: 2,
                                    color: '#8b5cf6', fontSize: '.56rem', fontWeight: 700, cursor: 'help'
                                  }}
                                >
                                  <Pause size={8} /> pausa {formatDuration(seg.pauseAfterSec)}
                                </span>
                              )}
                            </div>
                            {/* ── 3 Always-visible play buttons: Bruto / Preview / Premium ── */}
                            <div style={{ display: 'flex', gap: 2, flexShrink: 0 }}>
                              {/* Bruto — play original audio.webm segment */}
                              {(playSegmentBruto || playSegment) && (
                                <button
                                  onClick={() => {
                                    if (playingSegment && playingSegment.index === seg.index && playingSegment.type === 'original') {
                                      stopSegment?.();
                                    } else if (playSegmentBruto) {
                                      playSegmentBruto(seg.start, seg.end, seg.index);
                                    } else if (playSegment && previewUrl) {
                                      playSegment(previewUrl, seg.start, seg.end, seg.index, 'original');
                                    }
                                  }}
                                  style={{
                                    background: (playingSegment?.index === seg.index && playingSegment?.type === 'original')
                                      ? '#ef4444' : audioSource === 'bruto' ? 'rgba(148, 163, 184, 0.15)' : '#1e293b',
                                    color: audioSource === 'bruto' ? '#cbd5e1' : '#64748b',
                                    border: audioSource === 'bruto' ? '1px solid rgba(148, 163, 184, 0.3)' : '1px solid #334155',
                                    borderRadius: 3, padding: '2px 4px', cursor: 'pointer',
                                    display: 'flex', alignItems: 'center',
                                    transition: 'all 0.15s', fontSize: '.56rem'
                                  }}
                                  title="Bruto (áudio original do microfone)"
                                >
                                  {(playingSegment?.index === seg.index && playingSegment?.type === 'original') ? <Pause size={9} /> : <Volume2 size={9} />}
                                </button>
                              )}

                              {/* Preview — TTS via Web Speech API */}
                              {(() => {
                                const isPlayingPreview = segAudio?.playingSegIdx === seg.index && segAudio?.playingSource === 'preview';
                                const currentTtsVoice = ttsSelectedVoiceId || '';
                                const previewDone = segAudio?.getGenerationStatus(seg.index, 'preview', currentTtsVoice) === 'done';
                                const previewGenerating = segAudio?.getGenerationStatus(seg.index, 'preview', currentTtsVoice) === 'generating';
                                const previewNeedsGen = segAudio?.needsGeneration(seg.index, seg.text, 'preview', currentTtsVoice) !== false;
                                const previewHasVersion = currentTtsVoice && segAudio?.hasGeneratedVersion?.(seg.index, 'preview', currentTtsVoice);
                                return (
                                  <button
                                    onClick={() => {
                                      if (isPlayingPreview) {
                                        segAudio?.stopPlayback();
                                      } else if (previewDone && !previewNeedsGen) {
                                        segAudio?.playSegmentAudio(seg.index, 'preview', ttsGetVoice);
                                      } else {
                                        segAudio?.generatePreview(seg.index, seg.text, ttsSelectedVoiceId || '', ttsGetVoice || (() => null));
                                      }
                                    }}
                                    disabled={previewGenerating}
                                    style={{
                                      background: isPlayingPreview ? '#ef4444'
                                        : previewGenerating ? 'rgba(234, 179, 8, 0.08)'
                                        : audioSource === 'preview' ? 'rgba(234, 179, 8, 0.12)' : '#1e293b',
                                      color: isPlayingPreview ? '#fff'
                                        : previewGenerating ? '#92400e'
                                        : audioSource === 'preview' ? '#facc15' : '#64748b',
                                      border: audioSource === 'preview' ? '1px solid rgba(234, 179, 8, 0.3)' : '1px solid #334155',
                                      borderRadius: 3, padding: '2px 4px', cursor: previewGenerating ? 'wait' : 'pointer',
                                      display: 'flex', alignItems: 'center', gap: 1,
                                      transition: 'all 0.15s', fontSize: '.56rem'
                                    }}
                                    title={isPlayingPreview ? 'Parar TTS' : previewDone && !previewNeedsGen ? 'Ouvir TTS' : 'Gerar TTS Preview'}
                                  >
                                    {previewGenerating ? <Loader2 size={9} className="animate-spin" /> : isPlayingPreview ? <Pause size={9} /> : <Volume1 size={9} />}
                                    {previewHasVersion && !previewGenerating && <CheckCircle2 size={7} style={{ color: '#facc15' }} />}
                                  </button>
                                );
                              })()}

                              {/* Premium — Gemini AI */}
                              {(() => {
                                const isPlayingPremium = segAudio?.playingSegIdx === seg.index && segAudio?.playingSource === 'premium';
                                const premiumDone = segAudio?.getGenerationStatus(seg.index, 'premium', premiumVoiceId) === 'done';
                                const premiumNeedsGen = segAudio?.needsGeneration(seg.index, seg.text, 'premium', premiumVoiceId) !== false;
                                const premiumGenerating = segAudio?.getGenerationStatus(seg.index, 'premium', premiumVoiceId) === 'generating';
                                const premiumHasVersion = premiumVoiceId && segAudio?.hasGeneratedVersion?.(seg.index, 'premium', premiumVoiceId);
                                return (
                                  <button
                                    onClick={() => {
                                      if (isPlayingPremium) {
                                        segAudio?.stopPlayback();
                                      } else if (premiumDone && !premiumNeedsGen) {
                                        segAudio?.playSegmentAudio(seg.index, 'premium');
                                      } else {
                                        segAudio?.generatePremium(seg.index, seg.text, premiumVoiceId);
                                      }
                                    }}
                                    disabled={premiumGenerating}
                                    style={{
                                      background: isPlayingPremium ? '#ef4444'
                                        : premiumGenerating ? 'rgba(34, 197, 94, 0.08)'
                                        : audioSource === 'premium' ? 'rgba(34, 197, 94, 0.12)' : '#1e293b',
                                      color: isPlayingPremium ? '#fff'
                                        : premiumGenerating ? '#166534'
                                        : audioSource === 'premium' ? '#4ade80' : '#64748b',
                                      border: audioSource === 'premium' ? '1px solid rgba(34, 197, 94, 0.3)' : '1px solid #334155',
                                      borderRadius: 3, padding: '2px 4px', cursor: premiumGenerating ? 'wait' : 'pointer',
                                      display: 'flex', alignItems: 'center', gap: 1,
                                      transition: 'all 0.15s', fontSize: '.56rem'
                                    }}
                                    title={isPlayingPremium ? 'Parar Gemini' : premiumDone && !premiumNeedsGen ? `Ouvir ${premiumVoiceId}` : `Gerar áudio ${premiumVoiceId}`}
                                  >
                                    {premiumGenerating ? <Loader2 size={9} className="animate-spin" /> : isPlayingPremium ? <Pause size={9} /> : <Crown size={9} />}
                                    {premiumHasVersion && !premiumGenerating && <Crown size={7} style={{ color: '#4ade80' }} />}
                                  </button>
                                );
                              })()}
                            </div>
                          </div>

                          {/* Line 2: Text content — editable */}
                          {editingSegmentIndex === seg.index ? (
                            <div style={{ display: 'flex', gap: 4, marginTop: 2 }}>
                              <textarea
                                value={editingSegmentText}
                                onChange={(e) => setEditingSegmentText(e.target.value)}
                                autoFocus
                                style={{
                                  flex: 1, background: '#1e293b', color: '#f8fafc',
                                  border: '1px solid #38bdf8', borderRadius: 4,
                                  padding: '3px 6px', fontSize: '.75rem', resize: 'none',
                                  height: '42px', lineHeight: '1.3'
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' && !e.shiftKey) {
                                    e.preventDefault();
                                    handleUpdateSegment(seg.index, editingSegmentText);
                                    setEditingSegmentIndex(null);
                                  } else if (e.key === 'Escape') {
                                    setEditingSegmentIndex(null);
                                  }
                                }}
                              />
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                <button
                                  onClick={() => {
                                    handleUpdateSegment(seg.index, editingSegmentText);
                                    setEditingSegmentIndex(null);
                                  }}
                                  style={{ padding: '3px 6px', background: '#22c55e', color: '#0f172a', border: 'none', borderRadius: 3, fontSize: '.64rem', fontWeight: 700, cursor: 'pointer' }}
                                >OK</button>
                                <button
                                  onClick={() => setEditingSegmentIndex(null)}
                                  style={{ padding: '3px 6px', background: '#ef4444', color: '#fff', border: 'none', borderRadius: 3, fontSize: '.64rem', fontWeight: 700, cursor: 'pointer' }}
                                >X</button>
                              </div>
                            </div>
                          ) : (
                            <p
                              onClick={() => { setEditingSegmentIndex(seg.index); setEditingSegmentText(seg.text); }}
                              title="Clique para editar"
                              style={{ margin: 0, fontSize: '.74rem', color: '#e2e8f0', cursor: 'pointer', padding: '2px 3px', borderRadius: 3, transition: 'background 0.2s', lineHeight: '1.35' }}
                              onMouseEnter={(e) => e.currentTarget.style.background = '#1e293b'}
                              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                            >
                              {seg.text || <em style={{ color: '#64748b' }}>[Clique para adicionar texto]</em>}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </React.Fragment>
                );
              })}
              {/* Premium Analysis card */}
              {activeVersionIndex !== -1 && refinementVersions[activeVersionIndex]?.analysis && (
                <div style={{
                  margin: '12px',
                  background: 'rgba(124, 58, 237, 0.06)',
                  border: '1px solid rgba(124, 58, 237, 0.2)',
                  borderRadius: 8,
                  padding: '12px',
                  fontSize: '.72rem',
                  color: '#e2e8f0',
                  lineHeight: '1.45',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 700, color: '#c084fc', marginBottom: 6 }}>
                    <Sparkles size={12} />
                    Análise do Aprimoramento (Gemini 2.5 Pro)
                  </div>
                  <div style={{ color: '#cbd5e1', whiteSpace: 'pre-wrap', fontFamily: 'system-ui, sans-serif' }}>
                    {refinementVersions[activeVersionIndex].analysis}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div style={{
              flex: 1, border: '1px solid #334155', borderTop: 'none', borderBottom: 'none',
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              <textarea
                value={videoScript}
                onChange={(e) => setVideoScript(e.target.value)}
                placeholder="Transcreva ou digite o roteiro..."
                style={{
                  width: '100%', height: '100%', padding: '8px 12px', borderRadius: 0,
                  border: 'none', background: '#1e293b', color: '#f8fafc',
                  fontSize: '.78rem', resize: 'none', lineHeight: '1.4'
                }}
              />
            </div>
          )}

          {/* ── Stats bar — flush below segments ── */}
          <div style={{
            display: 'flex', gap: 10, flexWrap: 'wrap',
            padding: '5px 10px', background: '#0f172a',
            borderRadius: '0 0 8px 8px',
            border: '1px solid #334155', borderTop: 'none',
            fontSize: '.64rem', color: '#64748b', flexShrink: 0
          }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <BarChart3 size={9} /> {segmentsMeta.length} seg
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <FileText size={9} /> {totalWords} pal
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <Timer size={9} /> {formatDuration(totalDuration)}
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <Pause size={9} /> {formatDuration(totalPauses)} pausas
            </span>
            {overflowCount > 0 && (
              <button
                onClick={() => props.onFixOverflows?.()}
                title="Esticar timestamps para acomodar o áudio — resolve todos os overflows"
                style={{
                  display: 'flex', alignItems: 'center', gap: 3,
                  color: '#ef4444', fontWeight: 700, cursor: 'pointer',
                  background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.25)',
                  borderRadius: 4, padding: '1px 6px', fontSize: '.64rem',
                  transition: 'all 0.15s'
                }}
              >
                <AlertTriangle size={9} /> {overflowCount} overflow — clique para resolver
              </button>
            )}
          </div>
        </div>
      ) : (
        <div style={{
          display: 'flex', flexDirection: 'column', gap: 16,
          background: 'rgba(30, 41, 59, 0.4)', border: '1px solid #334155',
          borderRadius: 12, padding: 20, color: '#e2e8f0', lineHeight: '1.5', overflowY: 'auto'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#38bdf8' }}>
            <Sparkles size={18} />
            <h3 style={{ fontSize: '.9rem', fontWeight: 800, margin: 0 }}>Recursos de Inteligência Artificial — AI Studio</h3>
          </div>
          
          <p style={{ fontSize: '.78rem', color: '#94a3b8', margin: 0 }}>
            O Aluno Conectado permite aprimorar seu vídeo utilizando IA de voz de última geração. Veja os benefícios disponíveis:
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 4 }}>
            {[
              { num: '1', title: 'Transcrever Voz para Texto', desc: 'Converte a fala gravada no vídeo bruto em texto editável e gera legendas sincronizadas automaticamente.', active: true },
              { num: '2', title: 'Aprimorar Roteiro por IA', desc: 'Corrige a ortografia, ajusta pausas de respiração e melhora a qualidade da fala e pronúncia.', active: true },
              { num: '3', title: 'Processar com Voz Superior', desc: 'Gera um áudio em alta definição com a voz de sua escolha e mixa-o de volta ao seu vídeo final.', active: true },
              { num: '4', title: 'Pular e Salvar Direto (Sem IA)', desc: 'Se preferir manter a gravação bruta original com a sua própria voz, basta pular direto para a aba Vincular ao Catálogo ou salvar como avulso.', active: false }
            ].map((item) => (
              <div key={item.num} style={{ display: 'flex', gap: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 24, borderRadius: '50%', background: item.active ? 'rgba(56, 189, 248, 0.1)' : 'rgba(148, 163, 184, 0.1)', color: item.active ? '#38bdf8' : '#94a3b8', fontSize: '.75rem', fontWeight: 900, flexShrink: 0 }}>{item.num}</div>
                <div>
                  <h4 style={{ fontSize: '.78rem', fontWeight: 700, margin: '0 0 2px 0', color: item.active ? '#f1f5f9' : '#94a3b8' }}>{item.title}</h4>
                  <p style={{ fontSize: '.72rem', color: item.active ? '#94a3b8' : '#64748b', margin: 0 }}>{item.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <div style={{ height: '1px', background: '#334155', margin: '6px 0' }} />

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'center' }}>
            <button
              onClick={handleTranscribe}
              disabled={isTranscribing || (!videoBlob && !previewUrl)}
              style={{
                width: '100%', padding: '10px 16px', borderRadius: 8, border: 'none',
                background: '#38bdf8', color: '#0f172a', fontSize: '.8rem', fontWeight: 700,
                cursor: ((!videoBlob && !previewUrl) || isTranscribing) ? 'not-allowed' : 'pointer',
                opacity: ((!videoBlob && !previewUrl) || isTranscribing) ? 0.5 : 1,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                transition: 'all 0.2s', boxShadow: '0 2px 8px rgba(56, 189, 248, 0.25)'
              }}
            >
              {isTranscribing ? <Loader2 size={14} className="animate-spin" /> : <Mic size={14} />} 
              Transcrever Áudio do Vídeo para Iniciar
            </button>
            
            <button
              onClick={() => setShowManualInput(true)}
              style={{
                background: 'transparent', border: 'none', color: '#94a3b8',
                fontSize: '.72rem', cursor: 'pointer', textDecoration: 'underline'
              }}
            >
              Ou digitar roteiro manualmente
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
